import InfoTooltip from 'elements/tooltip/InfoTooltip'
import React, { useMemo } from 'react'
import {
  TooltipContainer,
  TooltipDescriptionCell,
  TooltipFooter,
  TooltipHeader,
  TooltipIconWrapper,
  TooltipLabel,
  TooltipRow,
  TooltipTable,
} from '../ProjectConfigurations.styles'
import { getDisplayName, getSnowLossDefaultsForCountry } from '../snowLossHelpers'
import { doNotTranslate } from 'util/misc'

interface SnowLossPercentageTooltipProps {
  countryIso2: string
}

export const SnowLossPercentageTooltip: React.FC<SnowLossPercentageTooltipProps> = ({ countryIso2 }) => {
  const tooltipContent = useMemo(() => {
    const defaults = getSnowLossDefaultsForCountry(countryIso2)
    if (Object.keys(defaults).length === 0) return null

    // Group locations by loss percentage, using display names when available
    const groupedByLoss: Record<number, string[]> = {}
    Object.entries(defaults).forEach(([locationCode, value]) => {
      if (!groupedByLoss[value]) {
        groupedByLoss[value] = []
      }
      const displayName = getDisplayName(countryIso2, locationCode)
      groupedByLoss[value].push(displayName)
    })

    // Sort by loss percentage (ascending) and locations alphabetically within each group
    const sortedGroups = Object.entries(groupedByLoss)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([value, locations]) => ({
        value: Number(value),
        locations: locations.sort((a, b) => a.localeCompare(b)),
      }))

    return (
      <TooltipContainer>
        <TooltipHeader>{doNotTranslate('Recommended Snow Loss (%) by State:')}</TooltipHeader>
        <TooltipTable>
          {sortedGroups.map(({ value, locations }) => (
            <TooltipRow key={value}>
              <TooltipLabel>{value}%</TooltipLabel>
              <TooltipDescriptionCell>{locations.join(', ')}</TooltipDescriptionCell>
            </TooltipRow>
          ))}
        </TooltipTable>
        <TooltipFooter>
          {doNotTranslate('These are recommended default values based on typical snow patterns for each state.')}
        </TooltipFooter>
      </TooltipContainer>
    )
  }, [countryIso2])

  if (!tooltipContent) return null

  return (
    <TooltipIconWrapper>
      <InfoTooltip placement="right" title={tooltipContent} severity="info" size="small" />
    </TooltipIconWrapper>
  )
}
